import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ShieldCheck, Clock, XCircle, Users, IndianRupee, CalendarCheck, Loader2, Check, X, Ticket, Trash2, Quote, Video, FileText } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import BlogCMS from "@/pages/AdminBlogCMS";
import FaqCMS from "@/pages/AdminFaqCMS";
import PagesCMS from "@/pages/AdminPagesCMS";
import MediaLibrary from "@/pages/AdminMediaLibrary";
import PlansCMS from "@/pages/AdminPlansCMS";

const STATUS_STYLE = {
  approved: "bg-emerald-500/15 text-emerald-600",
  verified: "bg-emerald-500/15 text-emerald-600",
  submitted: "bg-amber-500/15 text-amber-600",
  pending: "bg-amber-500/15 text-amber-600",
  interview_scheduled: "bg-blue-500/15 text-blue-600",
  in_process: "bg-blue-500/15 text-blue-600",
  rejected: "bg-destructive/15 text-destructive",
};

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [purohits, setPurohits] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [pricing, setPricing] = useState({ kundli: 0, dosha: 199, match: 251 });
  const [bookingPricing, setBookingPricing] = useState({ devotee_pct: 15, max_devotees: 9, plan_standard: 0, plan_premium: 1100, group_discount: 0, group_min: 4 });
  const [coupons, setCoupons] = useState([]);
  const [couponStats, setCouponStats] = useState({});
  const [newCoupon, setNewCoupon] = useState({ code: "", type: "percent", value: 10, min_amount: 0, max_uses: 0, expires_at: "" });
  const [quotes, setQuotes] = useState([]);
  const [newQuote, setNewQuote] = useState({ text: "", author: "", scheduled_date: "" });
  const [revenue, setRevenue] = useState(null);
  const [revRange, setRevRange] = useState("30d");
  const [pDocs, setPDocs] = useState({});
  const [interview, setInterview] = useState({});

  const load = () => {
    api.get("/admin/overview").then((r) => setOverview(r.data));
    api.get("/admin/purohits").then((r) => setPurohits(r.data));
    api.get("/admin/bookings").then((r) => setBookings(r.data));
    api.get("/admin/pricing").then((r) => setPricing(r.data));
    api.get("/admin/booking-pricing").then((r) => setBookingPricing(r.data));
    api.get("/admin/coupons").then((r) => setCoupons(r.data));
    api.get("/admin/coupon-stats").then((r) => setCouponStats(r.data));
    api.get("/admin/quotes").then((r) => setQuotes(r.data));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get(`/admin/revenue?range=${revRange}`).then((r) => setRevenue(r.data)).catch(() => {}); }, [revRange]);

  const savePricing = async () => {
    try { const r = await api.put("/admin/pricing", { kundli: Number(pricing.kundli), dosha: Number(pricing.dosha), match: Number(pricing.match), bundle_discount: Number(pricing.bundle_discount || 0), reminder_days: Number(pricing.reminder_days || 3) }); setPricing(r.data); toast.success("Settings updated"); }
    catch { toast.error("Could not update settings"); }
  };
  const saveBookingPricing = async () => {
    try {
      const r = await api.put("/admin/booking-pricing", {
        devotee_pct: Number(bookingPricing.devotee_pct),
        max_devotees: Number(bookingPricing.max_devotees),
        plan_standard: Number(bookingPricing.plan_standard),
        plan_premium: Number(bookingPricing.plan_premium),
        group_discount: Number(bookingPricing.group_discount),
        group_min: Number(bookingPricing.group_min),
      });
      setBookingPricing(r.data); toast.success("Booking pricing updated");
    } catch { toast.error("Could not update booking pricing"); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try { await api.post("/admin/brand-logo", { data: reader.result }); toast.success("Logo uploaded — it will appear on report PDFs"); }
      catch (e) { toast.error(e?.response?.data?.detail || "Could not upload logo"); }
    };
    reader.readAsDataURL(file);
  };
  const refreshCoupons = () => { api.get("/admin/coupons").then((r) => setCoupons(r.data)); api.get("/admin/coupon-stats").then((r) => setCouponStats(r.data)); };
  const createCoupon = async () => {
    try { await api.post("/admin/coupons", { ...newCoupon, value: Number(newCoupon.value), min_amount: Number(newCoupon.min_amount), max_uses: Number(newCoupon.max_uses), expires_at: newCoupon.expires_at || null }); toast.success("Coupon created"); setNewCoupon({ code: "", type: "percent", value: 10, min_amount: 0, max_uses: 0, expires_at: "" }); refreshCoupons(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create coupon"); }
  };
  const toggleCoupon = async (code) => { await api.post(`/admin/coupons/${code}/toggle`); refreshCoupons(); };
  const deleteCoupon = async (code) => { await api.delete(`/admin/coupons/${code}`); refreshCoupons(); };
  const refreshQuotes = () => api.get("/admin/quotes").then((r) => setQuotes(r.data));
  const createQuote = async () => {
    try { await api.post("/admin/quotes", newQuote); toast.success("Quote added"); setNewQuote({ text: "", author: "" }); refreshQuotes(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add quote"); }
  };
  const toggleQuote = async (id) => { await api.post(`/admin/quotes/${id}/toggle`); refreshQuotes(); };
  const deleteQuote = async (id) => { await api.delete(`/admin/quotes/${id}`); refreshQuotes(); };

  const act = async (id, action) => {
    const labels = { verify: "Purohit approved & onboarded", reject: "Purohit rejected", process: "Marked in process" };
    try { await api.post(`/admin/purohits/${id}/${action}`); toast.success(labels[action] || "Done"); load(); }
    catch { toast.error("Action failed"); }
  };
  const loadDocs = async (id) => {
    try { const r = await api.get(`/admin/purohits/${id}`); setPDocs((p) => ({ ...p, [id]: r.data })); }
    catch { toast.error("Could not load documents"); }
  };
  const scheduleInterview = async (id) => {
    const iv = interview[id] || {};
    if (!iv.zoom_link || !iv.scheduled_at) { toast.error("Enter a Zoom link and date/time"); return; }
    try { await api.post(`/admin/purohits/${id}/schedule-interview`, { zoom_link: iv.zoom_link, scheduled_at: iv.scheduled_at }); toast.success("Interview scheduled — purohit notified"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not schedule interview"); }
  };

  if (overview === null) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const cards = [
    { icon: Clock, label: "Pending Purohits", value: overview.pending_purohits, cls: "text-amber-600" },
    { icon: ShieldCheck, label: "Verified Purohits", value: overview.verified_purohits, cls: "text-emerald-600" },
    { icon: CalendarCheck, label: "Total Bookings", value: overview.total_bookings, cls: "text-primary" },
    { icon: IndianRupee, label: "Revenue", value: inr(overview.revenue), cls: "text-primary" },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-6xl mx-auto py-12" data-testid="admin-verification-dashboard">
      <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-2">Admin</p>
      <h1 className="font-serif text-3xl lg:text-4xl font-bold mb-8">Platform Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-border bg-card p-5">
            <c.icon className={`h-6 w-6 ${c.cls} mb-3`} />
            <p className="font-serif text-2xl font-bold">{c.value}</p>
            <p className="text-sm text-muted-foreground">{c.label}</p>
          </div>
        ))}
      </div>

      {revenue && (
        <div className="rounded-2xl border border-border bg-card p-6 mb-10" data-testid="revenue-dashboard">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div>
              <h3 className="font-serif text-lg font-semibold">Report sales & coupon impact</h3>
              <p className="text-sm text-muted-foreground mt-0.5" data-testid="revenue-totals">₹{revenue.totals.revenue.toLocaleString("en-IN")} revenue · {revenue.totals.sales} sales · ₹{revenue.totals.discount.toLocaleString("en-IN")} in discounts</p>
            </div>
            <div className="flex gap-1 bg-secondary rounded-full p-1">
              {[["30d", "Last 30 days"], ["12m", "Last 12 months"]].map(([r, label]) => (
                <button key={r} data-testid={`revenue-range-${r}`} onClick={() => setRevRange(r)}
                  className={`px-4 h-8 rounded-full text-sm transition-all ${revRange === r ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
              ))}
            </div>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <ComposedChart data={revenue.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={48} />
                <Tooltip formatter={(v, n) => [`₹${Number(v).toLocaleString("en-IN")}`, n]} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
                <Bar dataKey="revenue" name="Revenue" fill="#a1341a" radius={[4, 4, 0, 0]} maxBarSize={38} />
                <Line dataKey="discount" name="Discount" stroke="#c99a3f" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <Tabs defaultValue="purohits">
        <TabsList className="rounded-full mb-6">
          <TabsTrigger value="purohits" data-testid="admin-tab-purohits" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Purohit Verification</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Bookings</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="admin-tab-pricing" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Pricing</TabsTrigger>
          <TabsTrigger value="plans" data-testid="admin-tab-plans" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Plans</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="admin-tab-coupons" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Coupons</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="admin-tab-quotes" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Daily Quotes</TabsTrigger>
          <TabsTrigger value="blogs" data-testid="admin-tab-blogs" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Blog CMS</TabsTrigger>
          <TabsTrigger value="faqs" data-testid="admin-tab-faqs" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">FAQ</TabsTrigger>
          <TabsTrigger value="pages" data-testid="admin-tab-pages" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Pages</TabsTrigger>
          <TabsTrigger value="media" data-testid="admin-tab-media" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Media</TabsTrigger>
        </TabsList>

        <TabsContent value="purohits">
          <div className="space-y-3">
            {purohits.length === 0 && <p className="text-muted-foreground">No purohit registrations yet.</p>}
            {purohits.map((p) => {
              const done = p.status === "approved" || p.status === "verified" || p.status === "rejected";
              const doc = pDocs[p.id];
              const iv = interview[p.id] || {};
              return (
              <div key={p.id} data-testid={`admin-purohit-${p.id}`} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  <img src={p.image} alt="" className="h-14 w-14 rounded-full object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-serif font-semibold">{p.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE[p.status] || ""}`}>{(p.status || "").replace("_", " ")}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {p.age ? `${p.age} yrs · ` : ""}{p.gender ? `${p.gender} · ` : ""}{p.experience} yrs exp{p.email ? ` · ${p.email}` : ""}{p.phone ? ` · ${p.phone}` : ""}
                    </p>
                    {(p.id_type || p.education) && <p className="text-xs text-muted-foreground mt-0.5">{p.id_type ? `${p.id_type}: ${p.id_number}` : ""}{p.education ? ` · ${p.education}` : ""}{p.gurukul ? ` · ${p.gurukul}` : ""}</p>}
                    {p.languages?.length > 0 && <p className="text-xs text-muted-foreground mt-0.5">Languages: {p.languages.map((l) => `${l.language} (R${l.read}/W${l.write}/S${l.speak})`).join(", ")}</p>}
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.bio}</p>
                    {p.interview && <p className="text-xs text-blue-600 mt-1">Interview: {p.interview.scheduled_at}</p>}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {p.status !== "verified" && p.status !== "approved" && <Button size="sm" data-testid={`verify-${p.id}`} onClick={() => act(p.id, "verify")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="h-4 w-4 mr-1" /> Approve</Button>}
                    {!done && <Button size="sm" variant="outline" data-testid={`process-${p.id}`} onClick={() => act(p.id, "process")} className="rounded-full border-blue-500/40 text-blue-600 hover:bg-blue-600 hover:text-white">In process</Button>}
                    {p.status !== "rejected" && <Button size="sm" variant="outline" data-testid={`reject-${p.id}`} onClick={() => act(p.id, "reject")} className="rounded-full text-destructive border-destructive/40 hover:bg-destructive hover:text-white"><X className="h-4 w-4 mr-1" /> Reject</Button>}
                  </div>
                </div>

                {/* Documents + interview scheduling */}
                <div className="mt-4 pt-4 border-t border-border grid lg:grid-cols-2 gap-4">
                  <div>
                    {!doc ? (
                      <Button size="sm" variant="ghost" data-testid={`load-docs-${p.id}`} onClick={() => loadDocs(p.id)} className="rounded-full text-primary"><FileText className="h-4 w-4 mr-1.5" /> View uploaded documents</Button>
                    ) : (
                      <div className="flex flex-wrap gap-3 items-center" data-testid={`docs-${p.id}`}>
                        {doc.profile_pic && <img src={doc.profile_pic} alt="profile" className="h-14 w-14 rounded-lg object-cover border border-border" />}
                        {doc.biodata_pdf ? <a href={doc.biodata_pdf} download={`${p.name}-biodata.pdf`} data-testid={`biodata-${p.id}`} className="text-sm text-primary hover:underline flex items-center gap-1"><FileText className="h-4 w-4" /> Biodata</a> : <span className="text-xs text-muted-foreground">No biodata</span>}
                        {doc.id_card_pdf ? <a href={doc.id_card_pdf} download={`${p.name}-id.pdf`} data-testid={`idcard-${p.id}`} className="text-sm text-primary hover:underline flex items-center gap-1"><FileText className="h-4 w-4" /> ID card</a> : <span className="text-xs text-muted-foreground">No ID card</span>}
                      </div>
                    )}
                  </div>
                  {!done && (
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                      <div className="flex-1"><Label className="mb-1 block text-xs">Zoom link</Label><Input data-testid={`zoom-link-${p.id}`} value={iv.zoom_link || ""} onChange={(e) => setInterview((s) => ({ ...s, [p.id]: { ...s[p.id], zoom_link: e.target.value } }))} className="rounded-xl h-9" placeholder="https://zoom.us/j/…" /></div>
                      <div><Label className="mb-1 block text-xs">Date & time</Label><Input type="datetime-local" data-testid={`zoom-time-${p.id}`} value={iv.scheduled_at || ""} onChange={(e) => setInterview((s) => ({ ...s, [p.id]: { ...s[p.id], scheduled_at: e.target.value } }))} className="rounded-xl h-9" /></div>
                      <Button size="sm" data-testid={`schedule-${p.id}`} onClick={() => scheduleInterview(p.id)} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white"><Video className="h-4 w-4 mr-1" /> Schedule</Button>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="bookings">
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60 text-left"><tr><th className="p-3 font-serif">ID</th><th className="p-3 font-serif">Service</th><th className="p-3 font-serif">Devotee</th><th className="p-3 font-serif">Date</th><th className="p-3 font-serif">Amount</th><th className="p-3 font-serif">Status</th></tr></thead>
              <tbody>
                {bookings.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-muted-foreground">No bookings yet.</td></tr>}
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-border" data-testid={`admin-booking-${b.id}`}>
                    <td className="p-3 font-mono text-xs">{b.id}</td>
                    <td className="p-3">{b.service_name}</td>
                    <td className="p-3">{b.devotee?.name}</td>
                    <td className="p-3 text-muted-foreground">{b.date}</td>
                    <td className="p-3 font-medium">{inr(b.total)}</td>
                    <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE[b.status === "confirmed" || b.status === "completed" ? "verified" : b.status === "cancelled" ? "rejected" : "pending"]}`}>{b.status.replace("_", " ")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="pricing">
          <div className="rounded-2xl border border-border bg-card p-6 max-w-lg" data-testid="admin-pricing-panel">
            <h3 className="font-serif text-lg font-semibold mb-4">Vedic Report Prices (₹)</h3>
            <div className="space-y-4">
              {[["kundli", "Rashi & Kundli"], ["dosha", "Dosha & Remedies"], ["match", "Kundli Matchmaking"], ["bundle_discount", "Bundle discount (Kundli + Dosha)"], ["reminder_days", "Dosha reminder (days after purchase)"]].map(([k, label]) => (
                <div key={k} className="flex items-center justify-between gap-4">
                  <Label className="flex-1">{label}</Label>
                  <Input type="number" min="0" data-testid={`price-${k}`} value={pricing[k] ?? 0} onChange={(e) => setPricing({ ...pricing, [k]: e.target.value })} className="rounded-xl w-32" />
                </div>
              ))}
            </div>
            <Button onClick={savePricing} data-testid="save-pricing-btn" className="mt-6 rounded-full bg-primary text-primary-foreground">Save settings</Button>
            <p className="text-xs text-muted-foreground mt-3">Set a price to 0 to make that report free. Bundle discount applies when both Kundli & Dosha are chosen together.</p>

            <div className="mt-6 pt-6 border-t border-border">
              <h4 className="font-serif font-semibold mb-1">Brand logo on PDFs</h4>
              <p className="text-xs text-muted-foreground mb-3">Upload a PNG/JPG (max 2MB). It appears on every report PDF.</p>
              <input type="file" accept="image/png,image/jpeg" data-testid="logo-upload" onChange={(e) => uploadLogo(e.target.files[0])} className="text-sm" />
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 max-w-lg mt-6" data-testid="admin-booking-pricing-panel">
            <h3 className="font-serif text-lg font-semibold mb-1">Worship / Ritual Booking Pricing</h3>
            <p className="text-xs text-muted-foreground mb-4">Controls how the booking total scales with the number of devotees and the plan add-ons.</p>
            <div className="space-y-4">
              {[["devotee_pct", "Price increase per extra devotee (%)"], ["max_devotees", "Maximum devotees per booking"], ["plan_standard", "Standard plan add-on (₹)"], ["plan_premium", "Premium plan add-on (₹)"], ["group_discount", "Group discount per devotee (₹)"], ["group_min", "Group discount applies from N devotees"]].map(([k, label]) => (
                <div key={k} className="flex items-center justify-between gap-4">
                  <Label className="flex-1">{label}</Label>
                  <Input type="number" min="0" data-testid={`booking-price-${k}`} value={bookingPricing[k] ?? 0} onChange={(e) => setBookingPricing({ ...bookingPricing, [k]: e.target.value })} className="rounded-xl w-32" />
                </div>
              ))}
            </div>
            <Button onClick={saveBookingPricing} data-testid="save-booking-pricing-btn" className="mt-6 rounded-full bg-primary text-primary-foreground">Save booking pricing</Button>
            <p className="text-xs text-muted-foreground mt-3">Base &amp; plan amounts multiply by (1 + {bookingPricing.devotee_pct || 0}% × extra devotees). A group discount of ₹{bookingPricing.group_discount || 0}/devotee applies from {bookingPricing.group_min || 4} devotees. GST 5% applies on the final total.</p>
          </div>
        </TabsContent>

        <TabsContent value="coupons">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="admin-coupon-form">
              <h3 className="font-serif text-lg font-semibold mb-4">Create coupon</h3>
              <div className="space-y-3">
                <div><Label className="mb-1.5 block">Code</Label><Input data-testid="coupon-code" value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })} className="rounded-xl" placeholder="FESTIVE20" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="mb-1.5 block">Type</Label>
                    <select data-testid="coupon-type" value={newCoupon.type} onChange={(e) => setNewCoupon({ ...newCoupon, type: e.target.value })} className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm">
                      <option value="percent">Percent (%)</option><option value="flat">Flat (₹)</option>
                    </select>
                  </div>
                  <div><Label className="mb-1.5 block">Value</Label><Input type="number" data-testid="coupon-value" value={newCoupon.value} onChange={(e) => setNewCoupon({ ...newCoupon, value: e.target.value })} className="rounded-xl" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="mb-1.5 block">Min amount ₹</Label><Input type="number" data-testid="coupon-min" value={newCoupon.min_amount} onChange={(e) => setNewCoupon({ ...newCoupon, min_amount: e.target.value })} className="rounded-xl" /></div>
                  <div><Label className="mb-1.5 block">Max uses (0=∞)</Label><Input type="number" data-testid="coupon-maxuses" value={newCoupon.max_uses} onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: e.target.value })} className="rounded-xl" /></div>
                </div>
                <div><Label className="mb-1.5 block">Expiry date (optional)</Label><Input type="date" data-testid="coupon-expiry" value={newCoupon.expires_at} onChange={(e) => setNewCoupon({ ...newCoupon, expires_at: e.target.value })} className="rounded-xl" /></div>
                <Button onClick={createCoupon} data-testid="create-coupon-btn" className="w-full rounded-full bg-primary text-primary-foreground"><Ticket className="h-4 w-4 mr-2" /> Create coupon</Button>
              </div>
            </div>
            <div className="space-y-3" data-testid="admin-coupon-list">
              {coupons.length === 0 && <p className="text-muted-foreground">No coupons yet.</p>}
              {coupons.map((c) => {
                const expired = c.expires_at && c.expires_at < new Date().toISOString();
                return (
                <div key={c.code} data-testid={`coupon-row-${c.code}`} className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3">
                  <div className="flex-1">
                    <p className="font-mono font-semibold">{c.code}{" "}
                      <span className={`text-xs px-2 py-0.5 rounded-full ml-1 ${c.active ? "bg-emerald-500/15 text-emerald-600" : "bg-muted text-muted-foreground"}`}>{c.active ? "active" : "off"}</span>
                      {expired && <span data-testid={`coupon-expired-${c.code}`} className="text-xs px-2 py-0.5 rounded-full ml-1 bg-destructive/15 text-destructive">expired</span>}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`}{c.min_amount ? ` · min ₹${c.min_amount}` : ""}{c.max_uses ? ` · ${c.used_count}/${c.max_uses} used` : ` · ${c.used_count} used`}{c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : ""}</p>
                    {couponStats[c.code] && <p className="text-xs text-primary mt-0.5" data-testid={`coupon-stats-${c.code}`}>{couponStats[c.code].redemptions} redeemed · ₹{couponStats[c.code].revenue} revenue · ₹{couponStats[c.code].discount_given} given</p>}
                  </div>
                  <Button size="sm" variant="outline" data-testid={`toggle-coupon-${c.code}`} onClick={() => toggleCoupon(c.code)} className="rounded-full">{c.active ? "Disable" : "Enable"}</Button>
                  <Button size="sm" variant="outline" data-testid={`delete-coupon-${c.code}`} onClick={() => deleteCoupon(c.code)} className="rounded-full text-destructive border-destructive/40"><Trash2 className="h-4 w-4" /></Button>
                </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="quotes">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="admin-quote-form">
              <h3 className="font-serif text-lg font-semibold mb-4">Add daily quote</h3>
              <div className="space-y-3">
                <div><Label className="mb-1.5 block">Quote text</Label>
                  <textarea data-testid="quote-text-input" value={newQuote.text} onChange={(e) => setNewQuote({ ...newQuote, text: e.target.value })} rows={3} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" placeholder="You have the right to perform your actions…" /></div>
                <div><Label className="mb-1.5 block">Author / source</Label><Input data-testid="quote-author-input" value={newQuote.author} onChange={(e) => setNewQuote({ ...newQuote, author: e.target.value })} className="rounded-xl" placeholder="Bhagavad Gita 2.47" /></div>
                <div><Label className="mb-1.5 block">Quote of the day — schedule for date (optional)</Label><Input type="date" data-testid="quote-date-input" value={newQuote.scheduled_date} onChange={(e) => setNewQuote({ ...newQuote, scheduled_date: e.target.value })} className="rounded-xl" /></div>
                <Button onClick={createQuote} data-testid="create-quote-btn" className="w-full rounded-full bg-primary text-primary-foreground"><Quote className="h-4 w-4 mr-2" /> Add quote</Button>
                <p className="text-xs text-muted-foreground">Active quotes appear in the Divine Bless hero slider. A quote scheduled for today shows first, then the rest rotate.</p>
              </div>
            </div>
            <div className="space-y-3" data-testid="admin-quote-list">
              {quotes.length === 0 && <p className="text-muted-foreground">No quotes yet.</p>}
              {quotes.map((qt) => (
                <div key={qt.id} data-testid={`quote-row-${qt.id}`} className="rounded-2xl border border-border bg-card p-4 flex items-start gap-3">
                  <div className="flex-1">
                    <p className="text-sm">"{qt.text}"{" "}<span className={`text-xs px-2 py-0.5 rounded-full ${qt.active ? "bg-emerald-500/15 text-emerald-600" : "bg-muted text-muted-foreground"}`}>{qt.active ? "active" : "off"}</span></p>
                    {qt.author && <p className="text-xs text-accent mt-1">— {qt.author}</p>}
                    {qt.scheduled_date && <p className="text-xs text-primary mt-1" data-testid={`quote-scheduled-${qt.id}`}>Quote of the day · {new Date(qt.scheduled_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>}
                  </div>
                  <Button size="sm" variant="outline" data-testid={`toggle-quote-${qt.id}`} onClick={() => toggleQuote(qt.id)} className="rounded-full">{qt.active ? "Disable" : "Enable"}</Button>
                  <Button size="sm" variant="outline" data-testid={`delete-quote-${qt.id}`} onClick={() => deleteQuote(qt.id)} className="rounded-full text-destructive border-destructive/40"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="blogs">
          <BlogCMS />
        </TabsContent>

        <TabsContent value="faqs">
          <FaqCMS />
        </TabsContent>

        <TabsContent value="pages">
          <PagesCMS />
        </TabsContent>

        <TabsContent value="plans">
          <PlansCMS />
        </TabsContent>

        <TabsContent value="media">
          <MediaLibrary />
        </TabsContent>
      </Tabs>
    </div>
  );
}
